import { cva } from 'cva';
import {
  Slider as RACSlider,
  type SliderProps as RACSliderProps,
} from 'react-aria-components/Slider';

import styles from './slider.module.css';

const sliderVariants = cva({
  base: styles.slider,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

export type SliderProps<T> = RACSliderProps<T> & {
  size?: 's' | 'm';
};

/**
 * A slider allows a user to select one or more values within a range.
 *
 * See https://react-spectrum.adobe.com/react-aria/Slider.html for more details.
 *
 * ```tsx
 * <Slider>
 *   <SliderLabel>Opacity</SliderLabel>
 *   <SliderOutput />
 *   <SliderTrack>
 *    <SliderThumb />
 *   </SliderTrack>
 * </Slider>
 * ```
 */
export function Slider<T extends number | number[]>(props: SliderProps<T>) {
  const { className, size, ...rest } = props;
  const classes = sliderVariants({ size, className });
  return <RACSlider<T> className={classes} {...rest} />;
}
